/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: Admin data-audit dashboard. Uses "setState in async handler
 * after kicking off a fetch" patterns (e.g. setBusyPattern + setDryRunResult
 * before awaiting) that the experimental eslint-plugin-react-hooks v5 rule
 * mis-flags. Behaviour is intentional and audited.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Database, AlertTriangle, Trash2, RefreshCw, Lock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";

// Visibility + cleanup for the production user dataset. Shows exactly what
// is in MongoDB (top domains, recent signups, test-pattern heuristics) so
// the admin can see for themselves whether totals reflect real users or
// test leftovers. Cleanup is dry-run first and guarded server-side against
// removing any admin account.
export const DataAuditSection = ({ viewerIsPrimary }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPattern, setBusyPattern] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [orphanReport, setOrphanReport] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-audit");
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load data audit");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runCleanup = async (pattern, dry_run) => {
    setBusyPattern(pattern);
    if (dry_run) setDryRunResult(null);
    try {
      const { data } = await api.post("/admin/data-cleanup", { pattern, dry_run });
      if (dry_run) {
        setDryRunResult(data);
      } else {
        toast.success(`Deleted ${data.deleted_users} users (${data.pattern})`);
        setDryRunResult(null);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally { setBusyPattern(null); }
  };

  // Orphan cleanup — removes household-scoped data (households, messages,
  // children, expenses, requests, events, journal_entries, documents,
  // audit_log, mediator_invites) belonging to households with zero
  // remaining non-mediator users. Dry-run first, then confirm to delete.
  const runOrphanCleanup = async (dry_run) => {
    setOrphanBusy(true);
    if (dry_run) setOrphanReport(null);
    try {
      const { data } = await api.post(`/admin/data-cleanup-orphans?dry_run=${dry_run}`);
      if (dry_run) {
        setOrphanReport(data);
      } else {
        const total = Object.values(data.deleted || {}).reduce((a, b) => a + (b || 0), 0);
        toast.success(`Cleaned ${total.toLocaleString()} orphan records across ${Object.keys(data.deleted || {}).length} collections`);
        setOrphanReport(null);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Orphan cleanup failed");
    } finally { setOrphanBusy(false); }
  };

  return (
    <section className="space-y-3" data-testid="admin-data-audit-section">
      <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium inline-flex items-center gap-1.5">
        <Database className="w-3.5 h-3.5" /> Data audit · what&apos;s actually in the database
      </h2>

      <div className="border border-[#D9E1E3] bg-white rounded-md p-5 space-y-5">
        {loading ? (
          <div className="text-sm text-[#6F828A]">Loading…</div>
        ) : !audit ? (
          <div className="text-sm text-[#8C2A22]">Couldn&apos;t load audit data.</div>
        ) : (
          <>
            {/* Totals — sanity check against the analytics numbers above */}
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Total users in DB</div>
                <div className="font-heading text-2xl font-semibold tabular-nums text-[#2E3A3F]" data-testid="audit-total-users">{audit.total_users.toLocaleString()}</div>
                <div className="text-[11px] text-[#6F828A]">Non-mediator: <span className="font-mono-data tabular-nums text-[#2E3A3F]">{audit.total_non_mediator_users.toLocaleString()}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Primary admin (locked)</div>
                <div className="font-mono-data text-[13px] text-[#2E3A3F] break-all">{audit.primary_admin_email}</div>
              </div>
            </div>

            {/* Email-domain breakdown — instantly reveals test-heavy datasets */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Top email domains</div>
              <div className="border border-[#EEF3F4] rounded-sm divide-y divide-[#EEF3F4]" data-testid="audit-domains">
                {audit.email_domains.length === 0 ? (
                  <div className="p-3 text-sm text-[#6F828A]">No data.</div>
                ) : audit.email_domains.map((d) => (
                  <div key={d.domain} className="flex justify-between items-baseline px-3 py-1.5 text-sm">
                    <span className="font-mono-data text-[#2E3A3F]">@{d.domain}</span>
                    <span className="font-mono-data tabular-nums text-[#6F828A]">{d.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent signups — so you can eyeball the most recent 20 emails */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Most recent 20 signups</div>
              <div className="border border-[#EEF3F4] rounded-sm divide-y divide-[#EEF3F4] max-h-72 overflow-y-auto" data-testid="audit-recent">
                {audit.recent_users.length === 0 ? (
                  <div className="p-3 text-sm text-[#6F828A]">No users yet.</div>
                ) : audit.recent_users.map((u, i) => (
                  <div key={u.id || i} className="flex justify-between items-baseline px-3 py-1.5 text-sm gap-3">
                    <span className="font-mono-data text-[#2E3A3F] truncate">{u.email || "(no email)"}</span>
                    <span className="font-mono-data text-[11px] text-[#6F828A] shrink-0">{u.created_at ? formatDT(u.created_at) : "—"}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cleanup controls */}
            {audit.test_pattern_matches.length === 0 ? (
              <div className="flex items-start gap-2 bg-[#DCEFDC] border border-[#A9D3A9] rounded-sm px-3 py-3 text-sm text-[#1E5A1E]">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>No accounts matched known test-email patterns. If the totals look wrong, review the recent-signups list above.</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Detected test-pattern accounts</div>
                <div className="space-y-2" data-testid="audit-cleanup-rows">
                  {audit.test_pattern_matches.map((m) => (
                    <div key={m.key} className="flex flex-wrap items-center justify-between gap-2 border border-[#E6CFA2] bg-[#FDF4E2] rounded-sm px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-[#8F6B1F]" />
                        <span className="font-mono-data text-[#2E3A3F]">{m.label}</span>
                        <span className="text-[11px] text-[#6F828A] font-mono-data">{m.count.toLocaleString()} accounts</span>
                      </div>
                      {viewerIsPrimary ? (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busyPattern === m.key}
                            onClick={() => runCleanup(m.key, true)}
                            className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm"
                            data-testid={`audit-dryrun-${m.key}`}
                          >
                            {busyPattern === m.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Preview"}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#6F828A] inline-flex items-center gap-1"><Lock className="w-3 h-3" /> primary admin only</span>
                      )}
                    </div>
                  ))}
                </div>

                {dryRunResult && (
                  <div className="border border-[#D9E1E3] bg-white rounded-sm p-3 space-y-2" data-testid="audit-dryrun-result">
                    <div className="text-sm font-medium text-[#2E3A3F]">Preview: {dryRunResult.would_delete_count.toLocaleString()} accounts matching {dryRunResult.pattern}</div>
                    <div className="text-[11px] text-[#6F828A]">Sample of up to 25:</div>
                    <div className="max-h-48 overflow-y-auto border border-[#EEF3F4] rounded-sm divide-y divide-[#EEF3F4]">
                      {(dryRunResult.sample || []).map((u) => (
                        <div key={u.id} className="px-3 py-1.5 text-xs font-mono-data text-[#2E3A3F] truncate">{u.email}</div>
                      ))}
                      {(dryRunResult.sample || []).length === 0 && <div className="p-3 text-xs text-[#6F828A]">(no matches)</div>}
                    </div>
                    <div className="text-[11px] text-[#6F828A]">
                      Protected (never deleted): <span className="font-mono-data">{(dryRunResult.guard_emails_protected || []).join(", ")}</span>
                    </div>
                    {dryRunResult.would_delete_count > 0 && viewerIsPrimary && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (!window.confirm(`Permanently delete ${dryRunResult.would_delete_count} users matching ${dryRunResult.pattern}? This cannot be undone.`)) return;
                          const key = Object.keys({example_com:1,test_com:1,plus_test:1,localhost:1,mailinator:1}).find(k => dryRunResult.pattern.includes(k.replace("_","."))) || "example_com";
                          // Map the human label back to the key by finding which pattern matched
                          const matched = audit.test_pattern_matches.find(p => p.label === dryRunResult.pattern);
                          runCleanup(matched?.key || key, false);
                        }}
                        className="bg-[#C0291D] hover:bg-[#9C2119] text-white rounded-sm"
                        data-testid="audit-confirm-delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete these {dryRunResult.would_delete_count} accounts
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Orphan data cleanup — removes households/messages/children/etc
                whose household has zero remaining users. Only shown to the
                primary admin since it's a destructive cascade. */}
            {viewerIsPrimary && (
              <div className="space-y-2 pt-4 border-t border-[#EEF3F4]" data-testid="orphan-cleanup">
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Orphaned household data</div>
                <div className="flex flex-wrap items-center justify-between gap-2 border border-[#D9E1E3] bg-[#F7F9FA] rounded-sm px-3 py-2 text-sm">
                  <div className="text-[#2E3A3F]">
                    When users are deleted, their households / messages / children / expenses / etc. can be left behind. This finds and removes them.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={orphanBusy}
                    onClick={() => runOrphanCleanup(true)}
                    className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm"
                    data-testid="orphan-dryrun-btn"
                  >
                    {orphanBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Scan for orphans"}
                  </Button>
                </div>
                {orphanReport && (
                  <div className="border border-[#D9E1E3] bg-white rounded-sm p-3 space-y-2" data-testid="orphan-report">
                    <div className="text-sm text-[#2E3A3F]">
                      Active households: <span className="font-mono-data">{orphanReport.alive_household_count.toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      {Object.entries(orphanReport.orphan_document_counts || orphanReport.deleted || {}).map(([k, v]) => (
                        <div key={k} className="flex justify-between border border-[#EEF3F4] rounded-sm px-2 py-1.5">
                          <span className="text-[#6F828A]">{k}</span>
                          <span className={`font-mono-data tabular-nums ${v > 0 ? "text-[#8C2A22]" : "text-[#2E3A3F]"}`}>{v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    {(Object.values(orphanReport.orphan_document_counts || {}).some((v) => v > 0)) && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={orphanBusy}
                        onClick={() => {
                          const total = Object.values(orphanReport.orphan_document_counts || {}).reduce((a, b) => a + b, 0);
                          if (!window.confirm(`Permanently delete ${total.toLocaleString()} orphan records? This cannot be undone.`)) return;
                          runOrphanCleanup(false);
                        }}
                        className="bg-[#C0291D] hover:bg-[#9C2119] text-white rounded-sm"
                        data-testid="orphan-confirm-delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete all orphans
                      </Button>
                    )}
                    {!(Object.values(orphanReport.orphan_document_counts || {}).some((v) => v > 0)) && orphanReport.dry_run && (
                      <div className="text-xs text-[#1E5A1E]">No orphan data found. Your database is clean.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="pt-2 border-t border-[#EEF3F4] flex justify-between items-center">
              <div className="text-[11px] text-[#6F828A]">Generated: <span className="font-mono-data">{formatDT(audit.generated_at)}</span></div>
              <Button type="button" variant="outline" size="sm" onClick={load} className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] rounded-sm">Refresh</Button>
            </div>
          </>
        )}
      </div>
      <p className="text-[11px] text-[#6F828A] italic">
        These numbers come directly from MongoDB. There is no mock or seeded data in the code. If you see counts here that look like test data, use the preview → delete flow above to clean them. Every deletion is audit-logged.
      </p>
    </section>
  );
};
